import {
  alpha,
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowUpRightIcon from "assets/svg/ArrowUpRightIcon";
import CopyIcon from "assets/svg/CopyIcon";
import EllipseIcon from "assets/svg/EllipseIcon";
import DownloadIcon from "assets/svg/DownloadIcon";
import QueryIcon from "assets/svg/QueryIcon";
import QuickTransactionIcon from "assets/svg/QuickTransactionIcon";
import UserProfileIcon from "assets/svg/UserProfileIcon";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useEffect, useRef } from "react";
import { QRCode } from "react-qrcode-logo";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FETCH_PROFILE_TYPE,
  FETCH_WALLET_TYPE,
} from "reduxStore/actions/authActionType";
import { RootState } from "reduxStore/store";
import theme, { responsiveFontSize } from "themes/Theme";
import logoImg from "assets/images/logo.webp";

const QuickTransactionCard = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { wallet } = useSelector((state: RootState) => state.wallet);
  const dispatch = useDispatch();
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch({ type: FETCH_WALLET_TYPE });
    dispatch({ type: FETCH_PROFILE_TYPE });
  }, []);

  const handleCopyToClipboard = async () => {
    if (!wallet?.data?.address) return;

    try {
      await navigator.clipboard.writeText(wallet?.data?.address);
      toast.info("Address copied to clipboard successfully");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Box sx={commonStyle.cardStyle}>
      <Stack direction="row" alignItems="center" gap={1}>
        <QuickTransactionIcon />
        <Typography sx={commonStyle.cardTitleText}>
          Quick Transaction
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          my: 1,
          gap: { xs: 3, sm: 4 },
        }}
      >
        {/* Left column: profile info and address box */}
        <Stack
          direction="column"
          spacing={{ xs: 3, sm: 4 }}
          sx={{
            flex: 1,
            minWidth: 0,
            mt: { xs: 2, sm: 4 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Avatar
              sx={{
                width: { xs: 48, sm: 60 },
                height: { xs: 48, sm: 60 },
                borderRadius: "50%", // Ensure it's a circle
                bgcolor: "#fffede",
                border: "1px solid #ccc", // Added circle border
              }}
            >
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: responsiveFontSize(24),
                  textTransform: "uppercase", // Capitalize the first letter
                }}
              >
                {user?.data?.personal_details?.firstName?.charAt(0)}
              </Typography>
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: responsiveFontSize(24),
                  textTransform: "uppercase", // Capitalize the first letter
                }}
              >
                {user?.data?.personal_details?.lastName?.charAt(0)}
              </Typography>
            </Avatar>
            <Box>
              <Typography sx={styles.profileName}>
                {user?.data?.personal_details?.firstName}{" "}
                {user?.data?.personal_details?.lastName}
              </Typography>
              <Typography sx={styles.profileEmail}>
                {user?.data?.personal_details?.email}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              ...styles.hashTransactionStyle,
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "fit-content" },
            }}
          >
            <Typography sx={{ ...styles.hashTextStyle, width: "90%" }}>
              {wallet?.data?.address}
            </Typography>
            <IconButton onClick={handleCopyToClipboard}>
              <CopyIcon />
            </IconButton>
          </Box>
        </Stack>
        {/* Right column: QR code */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: { xs: "center", sm: "flex-start" },
            alignItems: "center",
            mt: { xs: 3, sm: 0 },
            width: { xs: "100%", sm: "auto" },
            cursor: "pointer",
            ":hover .download-btn": { opacity: 1 },
            ":hover .qr-blur": { filter: "blur(3px)" },
          }}
          ref={qrRef}
        >
          <Box className="qr-blur" sx={{ transition: "filter 0.2s" }}>
            <QRCode
              id="quick-transaction-qrcode"
              size={
                typeof window !== "undefined" && window.innerWidth < 600
                  ? 120
                  : 160
              }
              ecLevel="Q"
              value={wallet?.data?.address || ""}
              logoPadding={10}
              logoHeight={32}
              logoWidth={32}
              qrStyle="dots"
              logoPaddingStyle="circle"
              logoImage={logoImg}
            />
          </Box>
          <IconButton
            className="download-btn"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(18, 74, 82, 0.76)",
              opacity: 0,
              transition: "opacity 0.2s",
              zIndex: 2,
              boxShadow: 1,
              "&:hover": {
                background: "rgba(0, 221, 255, 0.5)",
              },
              p: 0.5,
              width: 60,
              height: 60,
            }}
            onClick={() => {
              if (qrRef.current) {
                const canvas = qrRef.current.querySelector("canvas");
                if (canvas) {
                  const url = canvas.toDataURL("image/png");
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `wallet-qrcode.png`;
                  a.click();
                }
              }
            }}
            title="Download QR Code"
          >
            <DownloadIcon color="#fff" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default QuickTransactionCard;

const styles = {
  profileImageContainer: {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderRadius: "50%",
    width: 80,
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 2,
  },
  profileName: {
    fontSize: responsiveFontSize(14.8),
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  profileEmail: {
    fontSize: responsiveFontSize(11.84),
    color: theme.palette.text.secondary,
    fontWeight: 400,
    marginTop: 0.5,
  },
  hashTransactionStyle: {
    border: "0.75px solid rgba(232, 232, 232, 1)",
    padding: "5px 10px",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    maxWidth: "100%",
  },
  hashTextStyle: {
    fontSize: responsiveFontSize(11.5), // reduced font size
    color: theme.palette.text.primary,
    width: "90%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 400,
    marginRight: 1,
  },
  sendReceiveButton: {
    border: "1px solid rgba(232, 232, 232, 1)",
    borderRadius: "20px",
  },
  sendReceivedBtnText: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.text.primary,
    fontWeight: 400,
    textTransform: "none",
    ml: 1,
    py: 1.5,
    "& span": {
      color: theme.palette.primary.main,
    },
  },
  avatarStyle: {
    width: 60,
    height: 60,
    aspectRatio: "1/1",
    objectFit: "cover",
  },
};
